import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMatterport } from '@/hooks/useMatterport';
import { ControlPanel } from './ControlPanel';
import { CameraInfo } from './CameraInfo';
import { SweepsList } from './SweepsList';
import { TagManager } from './TagManager';
import { QuickNavigation } from './QuickNavigation';
import { ViewerOverlay } from './ViewerOverlay';
import { ConnectionStatus } from './ConnectionStatus';
import { SettingsPanel } from './SettingsPanel';
import { GuidedTour } from './GuidedTour';
import { FloorSelector } from './FloorSelector';
import { LabelManager } from './LabelManager';
import { Label3DOverlay } from './Label3DOverlay';
import { ViewPositionManager } from './ViewPositionManager';
import { AreaManager } from './AreaManager';
import { TicketPanel } from './TicketPanel';
import { TicketsPanel } from './TicketsPanel';
import { EquipmentPanel } from './EquipmentPanel';
import { BuildingsPanel } from './BuildingsPanel';
import { InstrumentationPanel } from './InstrumentationPanel';
import CalibrationPanel from './CalibrationPanel';
import DrawingsPanel from './DrawingsPanel';
import ProceduresPanel from './ProceduresPanel';
import PlannedChangesPanel from './PlannedChangesPanel';
import PlannedChanges3DOverlay from './PlannedChanges3DOverlay';
import RiskExposurePanel from './RiskExposurePanel';
import CompetencyPanel from './CompetencyPanel';
import CompliancePanel from './CompliancePanel';
import DashboardOverlay from './DashboardOverlay';
import { TrainingModulesPanel } from './TrainingModulesPanel';
import { MaintenanceProceduresPanel } from './MaintenanceProceduresPanel';
import { MaintenanceDashboard } from './MaintenanceDashboard';
import { TrainingDashboardOverlay } from './TrainingDashboardOverlay';
import { ProgressPanel } from './ProgressPanel';
import { InteractiveQuizOverlay } from './InteractiveQuizOverlay';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { 
  Maximize2, 
  Minimize2, 
  PanelRightClose, 
  PanelRightOpen,
  Info,
  Box,
  Map,
  Home,
  Route,
  Settings,
  MapPin,
  Loader2,
  Tag,
  ChevronDown,
  Eye,
  X
} from 'lucide-react';

const DEFAULT_MODEL_ID = '6szR5xa384b';

export function MatterportViewer({ 
  modelId = DEFAULT_MODEL_ID,
  isActiveIssuesPanelOpen = false,
  onCloseActiveIssuesPanel,
  onOpenActiveIssuesPanel,
  isAssetsPanelOpen = false,
  onCloseAssetsPanel,
  isInstrumentationPanelOpen = false,
  onCloseInstrumentationPanel,
  isBuildingsPanelOpen = false,
  onCloseBuildingsPanel,
  onOpenBuildingsPanel,
  isCompliancePanelOpen = false,
  onCloseCompliancePanel,
  isReferencePanelOpen = false,
  onCloseReferencePanel,
  isWorkflowsPanelOpen = false,
  onCloseWorkflowsPanel,
  isPlannedChangesPanelOpen = false,
  onClosePlannedChangesPanel,
  onOpenPlannedChangesPanel,
  isRiskExposurePanelOpen = false,
  onCloseRiskExposurePanel,
  onOpenRiskExposurePanel,
  isCompetencyPanelOpen = false,
  onCloseCompetencyPanel,
  isTrainingModulesPanelOpen = false,
  onCloseTrainingModulesPanel,
  onOpenTrainingModulesPanel,
  isMaintenanceProceduresPanelOpen = false,
  onCloseMaintenanceProceduresPanel,
  onOpenMaintenanceProceduresPanel,
  isMaintenanceDashboardOpen = false,
  onCloseMaintenanceDashboard,
  onOpenMaintenanceDashboard,
  maintenanceDashboardTab = 'overview',
  onNavigateReady,
  onNavigateToTagReady,
  onNavigateToPositionReady,
  isPreviewMode = false,
  activeInteractiveQuiz = null,
  onCloseInteractiveQuiz,
  onPointerIntersectionChange,
  onCurrentSweepChange,
  onCameraPositionChange,
  onSdkInstanceChange,
  onRequestCreateTag,
  infoHotspots = [],
  onInfoHotspotClick,
}) {
  const iframeRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Sidebar default: closed for training tours, otherwise based on screen width
  const [showSidebar, setShowSidebar] = useState(false); // Will be set properly after tour loads
  const [iframeLoaded, setIframeLoaded] = useState(false);
  // Guided Tour state
  const [isGuidedTourOpen, setIsGuidedTourOpen] = useState(false);
  // Tags popover state
  const [isTagsPopoverOpen, setIsTagsPopoverOpen] = useState(false);
  // Scans popover state
  const [isScansPopoverOpen, setIsScansPopoverOpen] = useState(false);
  // Current building state for dashboard
  const [currentBuilding, setCurrentBuilding] = useState(null);
  // Progress panel state for e-learning
  const [isProgressPanelOpen, setIsProgressPanelOpen] = useState(false);
  // Interactive quiz state
  const [isInteractiveQuizActive, setIsInteractiveQuizActive] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  // Auth state for ticket panel and settings visibility
  const { isAuthenticated, isViewer, isAdmin, canEdit, user, token } = useAuth();
  // Get tour-specific overlay buttons from context
  const { currentTour, overlayButtons, addOverlayButton, updateOverlayButton, removeOverlayButton } = useTour();
  
  // Check if current tour is a surplus tour (simplified viewer)
  const isSurplusTour = currentTour?.industry?.toLowerCase() === 'surplus';
  
  // Check if current tour is a training tour (simplified viewer)
  const isTrainingTour = currentTour?.industry?.toLowerCase() === 'training';
  
  // Combined check for simplified viewer tours (training and surplus)
  const isSimplifiedViewerTour = isTrainingTour || isSurplusTour;
  
  // Industries that get full management features (including dashboard overlay)
  const fullFeatureIndustries = ['oil_gas', 'facility', 'commercial'];
  const hasFullIndustryFeatures = fullFeatureIndustries.includes(currentTour?.industry?.toLowerCase());
  
  // Show dashboard overlay for admins always, or for full-feature industries
  // In preview mode, admin sees what client sees (based on industry features only)
  // When not in preview mode, admin always sees dashboard for any tour
  // BUT never show for training/surplus tours - they have their own specific UI
  const showDashboardOverlay = isSimplifiedViewerTour 
    ? false  // Never show dashboard for training/surplus tours
    : (isPreviewMode 
        ? hasFullIndustryFeatures  // In preview: only show if client would see it
        : (isAdmin || hasFullIndustryFeatures));  // Not in preview: admin always sees it
  
  // Settings state - load from localStorage on init
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('matterport-settings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log('Loaded settings from localStorage:', parsed);
        return parsed;
      } catch (e) {
        console.warn('Failed to parse saved settings:', e);
      }
    }
    return {
      overlayButtons: { enabled: true, showLabels: true },
      guidedTour: { enabled: true },
      tour: { enabled: true, speed: 1.5, autoPlay: false },
      labels: { enabled: true },
    };
  });

  // Handle settings change from SettingsPanel - this gets called when settings change
  const handleSettingsChange = useCallback((newSettings) => {
    console.log('Settings changed - autoPlay:', newSettings?.tour?.autoPlay);
    setSettings(newSettings);
  }, []);

  // Floor state
  const [floors, setFloors] = useState([]);
  const [currentFloor, setCurrentFloor] = useState(null);

  // Set sidebar default based on tour type, screen width, and user role
  useEffect(() => {
    if (isSimplifiedViewerTour && !isAdmin) {
      setShowSidebar(false); // Closed for training/surplus tours (except admins)
    } else if (window.innerWidth >= 768) {
      setShowSidebar(true); // Open by default on larger screens
    }
  }, [isSimplifiedViewerTour, isAdmin, currentTour?.id]);

  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setShowSidebar(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch quizzes for all tours (not just training)
  useEffect(() => {
    const fetchQuizzes = async () => {
      if (!token || !currentTour?.id) return;
      
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/elearning/quizzes/tour/${currentTour.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setQuizzes(data);
        }
      } catch (err) {
        console.error('Failed to fetch quizzes:', err);
      }
    };
    
    fetchQuizzes();
  }, [token, currentTour?.id]);

  // Overlay button handlers - now use TourContext
  const handleAddOverlayButton = async (button) => {
    await addOverlayButton(button);
  };

  const handleRemoveOverlayButton = async (buttonId) => {
    await removeOverlayButton(buttonId);
  };

  const handleUpdateOverlayButton = async (buttonId, updates) => {
    await updateOverlayButton(buttonId, updates);
  };
  
  const {
    sdkInstance,
    connectionStatus,
    error,
    modelData,
    cameraPosition,
    sweeps,
    tags,
    customTags,
    quickNavPoints,
    currentSweep,
    pointerIntersection,
    lockedIntersection,
    isTagPlacementMode,
    pendingTagData,
    matterportTourStops,
    matterportLabels,
    openTagId,
    connect,
    getIframeSrc,
    navigateToSweep,
    navigateToTag,
    navigateToLabel,
    navigateToPosition,
    navigateToTourStop,
    navigateToModelLocation,
    createTag,
    createInfoHotspotTag,
    deleteTag,
    addQuickNavPoint,
    removeQuickNavPoint,
    startTagPlacement,
    cancelTagPlacement,
    placeTagAtIntersection,
    lockTagPosition,
    startTour,
    stopTour,
    zoomTo,
    getZoom,
    closeTag,
    setViewMode,
  } = useMatterport(modelId, currentTour?.id, { hideEditorTools: isSimplifiedViewerTour });

  // Reset iframe loaded state when modelId changes
  useEffect(() => {
    setIframeLoaded(false);
  }, [modelId]);

  // Handle iframe load and SDK connection
  useEffect(() => {
    if (iframeLoaded && iframeRef.current && connectionStatus === 'idle') {
      // Add a small delay to ensure iframe is fully ready
      const timer = setTimeout(() => {
        connect(iframeRef.current);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [iframeLoaded, connect, connectionStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Any cleanup needed
    };
  }, []);

  // Propagate pointer intersection to parent for quiz position capture
  useEffect(() => {
    onPointerIntersectionChange?.(pointerIntersection);
  }, [pointerIntersection, onPointerIntersectionChange]);

  // Propagate current sweep to parent
  useEffect(() => {
    onCurrentSweepChange?.(currentSweep?.id || currentSweep?.sid);
  }, [currentSweep, onCurrentSweepChange]);

  // Propagate camera position to parent for quiz location capture
  useEffect(() => {
    onCameraPositionChange?.(cameraPosition);
  }, [cameraPosition, onCameraPositionChange]);

  // Propagate SDK instance to parent for hotspot placement
  useEffect(() => {
    onSdkInstanceChange?.(sdkInstance);
  }, [sdkInstance, onSdkInstanceChange]);

  // Expose the createTag function to parent for external tag creation
  // This ensures the tags state is properly updated
  useEffect(() => {
    if (onRequestCreateTag && createInfoHotspotTag) {
      // Pass the createInfoHotspotTag function reference to parent for info hotspots
      onRequestCreateTag(createInfoHotspotTag);
    }
  }, [createInfoHotspotTag, onRequestCreateTag]);

  // Check if a click position is near any info hotspot
  const checkInfoHotspotClick = useCallback((clickPosition) => {
    if (!infoHotspots || infoHotspots.length === 0 || !clickPosition) return null;
    
    for (const hotspot of infoHotspots) {
      if (!hotspot.position) continue;
      
      const dx = clickPosition.x - hotspot.position.x;
      const dy = clickPosition.y - hotspot.position.y;
      const dz = clickPosition.z - hotspot.position.z;
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
      
      const radius = hotspot.click_radius || 1.5;
      if (distance <= radius) {
        console.log('Info hotspot clicked:', hotspot.label, 'distance:', distance.toFixed(2));
        return hotspot;
      }
    }
    return null;
  }, [infoHotspots]);

  // Check if an opened tag corresponds to an info hotspot
  useEffect(() => {
    if (!openTagId || !infoHotspots || infoHotspots.length === 0) return;
    
    console.log('Tag opened:', openTagId);
    console.log('Looking for match in', infoHotspots.length, 'info hotspots');
    
    // Check if the opened tag matches any info hotspot by matterport_tag_id
    let matchingHotspot = infoHotspots.find(h => h.matterport_tag_id === openTagId);
    
    // Fallback 1: Try to match by checking if the label is in the tag
    if (!matchingHotspot && tags?.length > 0) {
      const openedTag = tags.find(t => t.id === openTagId);
      if (openedTag?.label) {
        console.log('Trying label match. Tag label:', openedTag.label);
        matchingHotspot = infoHotspots.find(h => {
          // Check if hotspot label is in the tag label (tags have emoji prefix like "📍 My Label")
          return openedTag.label.includes(h.label);
        });
      }
    }
    
    // Fallback 2: Check by position proximity (useful if tag IDs got out of sync)
    if (!matchingHotspot && tags?.length > 0) {
      const openedTag = tags.find(t => t.id === openTagId);
      if (openedTag?.anchorPosition) {
        console.log('Trying position match for tag at:', openedTag.anchorPosition);
        matchingHotspot = infoHotspots.find(h => {
          if (!h.position) return false;
          const dist = Math.sqrt(
            Math.pow(h.position.x - openedTag.anchorPosition.x, 2) +
            Math.pow(h.position.y - openedTag.anchorPosition.y, 2) +
            Math.pow(h.position.z - openedTag.anchorPosition.z, 2)
          );
          return dist < 0.5; // Within 0.5 meters
        });
        if (matchingHotspot) {
          console.log('Found match by position proximity:', matchingHotspot.label);
        }
      }
    }
    
    if (matchingHotspot && onInfoHotspotClick) {
      console.log('Found matching info hotspot:', matchingHotspot.label);
      // Close the native tag popup and show our custom dialog
      if (closeTag) {
        closeTag();
      }
      
      // Navigate to the hotspot location with saved camera rotation
      if (matchingHotspot.sweep_id && navigateToSweep) {
        const navOptions = { 
          transition: 'fly',
          transitionTime: 1500 
        };
        if (matchingHotspot.camera_rotation) {
          navOptions.rotation = matchingHotspot.camera_rotation;
          console.log('Navigating with saved rotation:', matchingHotspot.camera_rotation);
        }
        navigateToSweep(matchingHotspot.sweep_id, navOptions);
      }
      
      onInfoHotspotClick(matchingHotspot);
    } else {
      console.log('No matching info hotspot found for tag:', openTagId);
    }
  }, [openTagId, infoHotspots, onInfoHotspotClick, closeTag, tags, navigateToSweep]);

  // Auto-start tour if enabled in settings
  useEffect(() => {
    console.log('Auto-tour check - connectionStatus:', connectionStatus, 'autoPlay:', settings?.tour?.autoPlay, 'startTour:', !!startTour);
    
    if (connectionStatus === 'connected' && settings?.tour?.autoPlay && startTour) {
      // Delay slightly to ensure model is fully loaded
      const timer = setTimeout(async () => {
        try {
          console.log('Auto-starting Matterport tour NOW...');
          await startTour();
          console.log('Auto-tour started successfully');
        } catch (e) {
          console.warn('Auto-tour failed to start:', e.message);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [connectionStatus, settings?.tour?.autoPlay, startTour]);

  // Navigate to starting position when SDK connects AND model is loaded
  const [hasNavigatedToStart, setHasNavigatedToStart] = React.useState(false);
  const navigationTimerRef = React.useRef(null);
  
  useEffect(() => {
    console.log('Starting position check:', {
      connectionStatus,
      hasModelData: !!modelData,
      navPointsCount: quickNavPoints?.length,
      hasNavigateToSweep: !!navigateToSweep,
      hasNavigatedToStart
    });
    
    // Must have: connected status, model loaded, nav points, and not already navigated
    if (connectionStatus === 'connected' && 
        modelData &&
        quickNavPoints && 
        quickNavPoints.length > 0 && 
        navigateToSweep && 
        !hasNavigatedToStart &&
        !navigationTimerRef.current) {
      
      // Find a point named "Starting Position", "Starting Point", "Scan 1", or use the first point
      const startingPoint = quickNavPoints.find(p => 
        p.name?.toLowerCase().includes('start') || 
        p.name?.toLowerCase() === 'scan 1'
      ) || quickNavPoints[0];
      
      console.log('Found starting point:', startingPoint);
      
      if (startingPoint && startingPoint.sweepId) {
        setHasNavigatedToStart(true);
        navigationTimerRef.current = setTimeout(async () => {
          try {
            console.log('Navigating to starting position:', startingPoint.name, 'sweep:', startingPoint.sweepId);
            await navigateToSweep(startingPoint.sweepId, { rotation: startingPoint.rotation });
            console.log('Successfully navigated to starting position');
          } catch (e) {
            console.warn('Failed to navigate to starting position:', e.message);
          }
          navigationTimerRef.current = null;
        }, 1500);
      }
    }
  }, [connectionStatus, modelData, quickNavPoints, navigateToSweep, hasNavigatedToStart]);
  
  // Reset navigation flag when tour changes
  useEffect(() => {
    setHasNavigatedToStart(false);
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }
  }, [currentTour?.id]);

  // Close tags popover when sweep changes (user navigation)
  useEffect(() => {
    if (currentSweep?.id) {
      setIsTagsPopoverOpen(false);
    }
  }, [currentSweep?.id]);

  // ESC key to close open tag popup
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && openTagId) {
        closeTag();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openTagId, closeTag]);

  // Expose navigateToSweep function to parent component for global search
  useEffect(() => {
    if (onNavigateReady) {
      onNavigateReady(navigateToSweep);
    }
  }, [onNavigateReady, navigateToSweep]);

  // Expose navigateToTag function to parent for info hotspot navigation
  useEffect(() => {
    if (onNavigateToTagReady) {
      onNavigateToTagReady(navigateToTag);
    }
  }, [onNavigateToTagReady, navigateToTag]);

  // Expose navigateToPosition function to parent for coordinate-based navigation
  useEffect(() => {
    if (onNavigateToPositionReady) {
      onNavigateToPositionReady(navigateToPosition);
    }
  }, [onNavigateToPositionReady, navigateToPosition]);

  // Handle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Generate iframe source with native UI settings
  // For training/surplus tours, hide editor tools even for editors
  const iframeSrc = getIframeSrc({
    hideUI: true,
    showHelp: settings?.nativeUI?.showHelp ?? false,
    showShare: settings?.nativeUI?.showShare ?? false,
    showVR: settings?.nativeUI?.showVR ?? false,
    showFullscreen: settings?.nativeUI?.showFullscreen ?? false,
    showBranding: settings?.nativeUI?.showBranding ?? false,
    hideEditorTools: isSimplifiedViewerTour, // Hide admin tools for training/surplus tours
  });
  const isSDKConnected = !!sdkInstance;

  return (
    <div className={`relative flex h-full w-full ${
      isFullscreen ? 'fixed inset-0 z-50 bg-background' : ''
    }`}>
      {/* Main Viewer Area */}
      <div className={`relative flex-1 min-w-0 ${
        showSidebar && !isFullscreen ? 'mr-0' : ''
      }`}>
        {/* Viewer Container */}
        <div className="relative h-full w-full bg-background rounded-lg border border-border">
          {/* Connection Status Overlay - Fully opaque to hide Matterport loading screen */}
          {connectionStatus !== 'connected' && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-background">
              <ConnectionStatus 
                status={connectionStatus} 
                error={error}
                modelId={modelId}
              />
            </div>
          )}

          {/* Dashboard Overlay - Top Center - For admins or full-feature industries */}
          {/* Note: DashboardOverlay positions itself and handles pointer-events internally */}
          {isAuthenticated && connectionStatus === 'connected' && showDashboardOverlay && (
            <DashboardOverlay
              onOpenPlannedChanges={onOpenPlannedChangesPanel}
              onOpenTickets={onOpenActiveIssuesPanel}
              onOpenRiskExposure={onOpenRiskExposurePanel}
              currentBuilding={currentBuilding}
              onNavigate={navigateToSweep}
            />
          )}

          {/* Training Dashboard Overlay - Only for training tours */}
          {isAuthenticated && connectionStatus === 'connected' && isTrainingTour && (
            <TrainingDashboardOverlay
              currentTour={currentTour}
              onOpenMaintenanceDashboard={onOpenMaintenanceDashboard}
              onOpenProgressPanel={() => setIsProgressPanelOpen(true)}
            />
          )}

          {/* Floating Progress Bar - REMOVED as it was interfering with bottom buttons */}

          {/* Matterport Iframe */}
          <iframe
            key={modelId}
            ref={iframeRef}
            src={iframeSrc}
            className="h-full w-full border-0"
            allow="fullscreen *; vr *"
            title="Matterport 3D Viewer"
            onLoad={() => setIframeLoaded(true)}
          />

          {/* Floating Close Tag Button - Shows when tag popup is open (including maximized image) */}
          {openTagId && (
            <div className="absolute top-4 right-4 z-50 pointer-events-auto">
              <Button
                variant="default"
                size="lg"
                className="rounded-full shadow-xl bg-black/80 hover:bg-black text-white border-2 border-white/30 gap-2 px-4"
                onClick={() => closeTag()}
                title="Close Tag (ESC)"
              >
                <X className="h-5 w-5" />
                <span className="font-semibold">Close Tag</span>
              </Button>
            </div>
          )}

          {/* Logo overlays to cover native Matterport UI */}
          {/* Bottom bar - Straight line across the whole screen */}
          <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none">
            {/* Straight bar across the entire width - matches app background */}
            <div 
              className="absolute bottom-0 left-0 right-0 h-[95px]" 
              style={{ backgroundColor: 'hsl(var(--background))' }}
            />
            
            {/* Logo container */}
            <div className="relative h-[95px] pointer-events-none">
              {/* Left - Client Logo - 50px from left margin, fixed size container */}
              <div className="absolute left-[50px] bottom-0 h-full flex items-center pointer-events-auto">
                <div className="w-[180px] h-[70px] flex items-center justify-start">
                  {user?.client_logo_url && user.client_logo_url.trim() !== '' ? (
                    <img 
                      src={user.client_logo_url} 
                      alt={user?.client_name || 'Client'} 
                      className="max-w-[180px] max-h-[70px] w-auto h-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'block');
                      }}
                    />
                  ) : null}
                  <span 
                    className="text-foreground text-lg font-medium truncate"
                    style={{ display: user?.client_logo_url && user.client_logo_url.trim() !== '' ? 'none' : 'block' }}
                  >
                    {user?.client_name || ''}
                  </span>
                </div>
              </div>
              
              {/* Right - Zentara Logo - 50px from right margin, fixed size container */}
              <div className="absolute right-[50px] bottom-0 h-full flex items-center pointer-events-auto">
                <div className="w-[180px] h-[70px] flex items-center justify-end">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_7a888182-d9ad-4911-af25-412e3d52e004/artifacts/sstxximv_Zentara%20As-Ltd._-01.png" 
                    alt="Zentara" 
                    className="max-w-[180px] max-h-[70px] w-auto h-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tag Placement Mode Overlay - Two-step: Lock position, then place */}
          {isTagPlacementMode && (
            <div className="absolute inset-0 z-30">
              {/* Top banner with instructions */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-3 rounded-lg shadow-lg pointer-events-auto">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 animate-pulse" />
                  <div>
                    <p className="text-sm font-medium">Placing: &quot;{pendingTagData?.label}&quot;</p>
                    <p className="text-xs opacity-80">
                      {lockedIntersection 
                        ? "Position locked! Click 'Place Tag' to confirm" 
                        : "Hover over the equipment and click 'Lock Position'"}
                    </p>
                  </div>
                  <Button 
                    variant="secondary" 
                    size="sm"
                    className="ml-4"
                    onClick={cancelTagPlacement}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
              
              {/* Position indicator at bottom */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-background/95 text-foreground px-4 py-3 rounded-lg shadow-lg pointer-events-auto border">
                {!lockedIntersection ? (
                  // Step 1: Lock position
                  pointerIntersection ? (
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground mb-1">Current Target</p>
                        <span className="text-sm font-mono bg-muted px-3 py-1 rounded block">
                          X: {pointerIntersection.position?.x?.toFixed(2)}, 
                          Y: {pointerIntersection.position?.y?.toFixed(2)}, 
                          Z: {pointerIntersection.position?.z?.toFixed(2)}
                        </span>
                        {pointerIntersection.object && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Surface: {pointerIntersection.object.replace('intersectedobject.', '')}
                          </p>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => lockTagPosition(pointerIntersection)}
                        className="gap-2"
                      >
                        <MapPin className="h-4 w-4" />
                        Lock Position
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Move cursor over the 3D model...</span>
                    </div>
                  )
                ) : (
                  // Step 2: Confirm and place
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-xs text-green-600 mb-1 font-medium">✓ Position Locked</p>
                      <span className="text-sm font-mono bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded block">
                        X: {lockedIntersection.position?.x?.toFixed(2)}, 
                        Y: {lockedIntersection.position?.y?.toFixed(2)}, 
                        Z: {lockedIntersection.position?.z?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button 
                        size="sm" 
                        onClick={placeTagAtIntersection}
                        className="gap-2"
                      >
                        <MapPin className="h-4 w-4" />
                        Place Tag Here
                      </Button>
                      <Button 
                        variant="outline"
                        size="sm" 
                        onClick={() => lockTagPosition(null)}
                      >
                        Choose Different Position
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Overlay Navigation Buttons on the 3D Space - Visible to all users, edit controls only for editors */}
          {connectionStatus === 'connected' && settings?.overlayButtons?.enabled !== false && (
            <ViewerOverlay
              overlayButtons={overlayButtons}
              onNavigate={navigateToSweep}
              onAddButton={!isViewer ? handleAddOverlayButton : undefined}
              onRemoveButton={!isViewer ? handleRemoveOverlayButton : undefined}
              onUpdateButton={!isViewer ? handleUpdateOverlayButton : undefined}
              sweeps={sweeps}
              currentSweep={currentSweep}
              cameraPosition={cameraPosition}
              readOnly={isViewer}
              hideSettings={isSimplifiedViewerTour && !isAdmin}
            />
          )}

          {/* 3D Labels Overlay */}
          {connectionStatus === 'connected' && settings?.labels?.enabled !== false && (
            <Label3DOverlay
              cameraPosition={cameraPosition}
              enabled={settings?.labels?.enabled !== false}
            />
          )}

          {/* Area labels are created as SDK tags in App.js */}

          {/* Planned Changes 3D Overlay - Hidden in Preview Mode for simplified tours */}
          {connectionStatus === 'connected' && !isPreviewMode && (
            <PlannedChanges3DOverlay
              sdkInstance={sdkInstance}
              cameraPosition={cameraPosition}
              enabled={true}
              onChangeClick={(change) => {
                // Navigate to change position and open panel
                if (change.sweep_id) {
                  navigateToSweep(change.sweep_id, { rotation: change.rotation });
                }
                // Open the planned changes panel
                if (onOpenPlannedChangesPanel) {
                  onOpenPlannedChangesPanel();
                }
              }}
            />
          )}

          {/* Floating Controls */}
          {connectionStatus === 'connected' && (
            <>
              {/* Top-right controls */}
              <div className="absolute top-2 right-4 z-10 flex items-center gap-2">
                {/* Self Guided Tour Button - Always visible */}
                {isSDKConnected && settings?.guidedTour?.enabled !== false && (
                  <Button
                    variant="glass"
                    onClick={() => setIsGuidedTourOpen(true)}
                    className="rounded-lg gap-2 px-3"
                    title="Start Self Guided Tour"
                  >
                    <Route className="h-4 w-4" />
                    <span className="text-sm">Self Guided Tour</span>
                  </Button>
                )}

                {/* Show Tags Popover - Available to all users, stays open until clicked again */}
                {isSDKConnected && tags && tags.length > 0 && (
                  <Popover open={isTagsPopoverOpen} onOpenChange={setIsTagsPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="glass"
                        className="rounded-lg gap-2 px-3"
                        title="Show Tags"
                      >
                        <Tag className="h-4 w-4" />
                        <span className="text-sm">Show Tags</span>
                        <ChevronDown className={`h-3 w-3 opacity-50 transition-transform ${isTagsPopoverOpen ? 'rotate-180' : ''}`} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-80 max-h-[400px] overflow-y-auto p-0">
                      <div className="px-3 py-2 border-b bg-muted/50 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold">{tags.length} Tag{tags.length !== 1 ? 's' : ''} Available</p>
                          <p className="text-xs text-muted-foreground">Click a tag to navigate to it</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setIsTagsPopoverOpen(false)}
                          className="h-6 w-6 rounded-full"
                          title="Close"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="p-2 space-y-1">
                        {tags.map((tag) => (
                          <button
                            key={tag.id}
                            className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-accent transition-colors text-left"
                            onClick={async () => {
                              setIsTagsPopoverOpen(false);
                              await navigateToTag(tag.id);
                            }}
                          >
                            <Eye className="h-4 w-4 text-primary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{tag.label || 'Unnamed Tag'}</p>
                              {tag.description && (
                                <p className="text-xs text-muted-foreground line-clamp-2">{tag.description}</p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {/* Close Tag Button - Shows when a tag popup is open */}
                {isSDKConnected && openTagId && (
                  <Button
                    variant="glass"
                    className="rounded-lg gap-2 px-3 bg-destructive/20 hover:bg-destructive/30 border-destructive/50"
                    onClick={() => closeTag()}
                    title="Close Tag Popup"
                  >
                    <X className="h-4 w-4" />
                    <span className="text-sm">Close Tag</span>
                  </Button>
                )}

                {/* View Mode Buttons - Only for Admin/Editor, hidden in Preview Mode and for Viewers */}
                {isSDKConnected && canEdit && !isPreviewMode && (
                  <div className="glass rounded-lg p-1 flex gap-1 mr-2">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setViewMode('inside')}
                      className="rounded-md"
                      title="Inside View"
                    >
                      <Home className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setViewMode('dollhouse')}
                      className="rounded-md"
                      title="Dollhouse View"
                    >
                      <Box className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setViewMode('floorplan')}
                      className="rounded-md"
                      title="Floor Plan View"
                    >
                      <Map className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {/* Settings Panel - Only for Admin/Editor */}
                {canEdit && !isPreviewMode && (
                  <SettingsPanel
                    settings={settings}
                    onSettingsChange={handleSettingsChange}
                    sdkInstance={sdkInstance}
                    isSDKConnected={isSDKConnected}
                  />
                )}
                
                {/* Sidebar Toggle - Only for Admin/Editor */}
                {canEdit && !isPreviewMode && (
                  <Button
                    variant="glass"
                    size="icon"
                    onClick={() => setShowSidebar(!showSidebar)}
                    className="rounded-lg"
                  >
                    {showSidebar ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
                  </Button>
                )}
              </div>

              {/* Bottom control panel - Only for Admin/Editor */}
              {canEdit && (
                <ControlPanel
                  onStartTour={startTour}
                  onStopTour={stopTour}
                  onZoomIn={() => zoomTo(1.5)}
                  onZoomOut={() => zoomTo(0.5)}
                  sweeps={sweeps}
                  currentSweep={currentSweep}
                />
              )}

              {/* Camera Info Badge - Hidden for Viewers and Preview Mode - ALSO requires admin role */}
              {isAdmin && !isPreviewMode && <CameraInfo position={cameraPosition} />}
            </>
          )}
        </div>
      </div>

      {/* Sidebar - Only shown for Admin/Editor (canEdit), never for Viewers or Preview Mode */}
      {isAuthenticated && canEdit && !isPreviewMode && showSidebar && connectionStatus === 'connected' && (
        <div className={`
          ${isFullscreen ? 'w-96' : 'w-96'}
          flex-shrink-0 border-l border-border bg-card/50 backdrop-blur-sm
          animate-slide-in-right
        `}>
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-foreground">Space Details</h2>
                <Badge variant="outline" className="text-xs font-mono">
                  {modelData?.sid || modelId}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge 
                  variant={isSDKConnected ? 'default' : 'secondary'} 
                  className="text-[10px]"
                >
                  {isSDKConnected ? 'SDK Connected' : 'View Only'}
                </Badge>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Model loaded
                </p>
              </div>
            </div>

            {/* Quick Navigation */}
            <div className="p-4 border-b border-border">
              <QuickNavigation
                quickNavPoints={quickNavPoints}
                sweeps={sweeps}
                onNavigate={navigateToSweep}
                onAddNavPoint={addQuickNavPoint}
                onRemoveNavPoint={removeQuickNavPoint}
                sdkConnected={isSDKConnected}
              />
            </div>

            {/* Tabs */}
            <Tabs defaultValue="nav" className="flex-1 flex flex-col min-h-0">
              <TabsList className="mx-4 mt-4 grid grid-cols-3">
                <TabsTrigger value="nav">Nav</TabsTrigger>
                <TabsTrigger value="areas">Areas</TabsTrigger>
                <TabsTrigger value="tags">Tags</TabsTrigger>
              </TabsList>
              
              <TabsContent value="tags" className="flex-1 min-h-0 px-4 pb-4">
                <ScrollArea className="h-full">
                  <TagManager
                    tags={tags}
                    customTags={customTags}
                    cameraPosition={cameraPosition}
                    onCreateTag={createTag}
                    onDeleteTag={deleteTag}
                    onNavigateToTag={navigateToTag}
                    sdkConnected={isSDKConnected}
                    isTagPlacementMode={isTagPlacementMode}
                    onStartTagPlacement={startTagPlacement}
                    onCancelTagPlacement={cancelTagPlacement}
                    onPlaceTag={placeTagAtIntersection}
                    pointerIntersection={pointerIntersection}
                  />
                </ScrollArea>
              </TabsContent>

              <TabsContent value="areas" className="flex-1 min-h-0 px-4 pb-4">
                <ScrollArea className="h-full">
                  <AreaManager
                    sweeps={sweeps}
                    currentSweep={currentSweep}
                    cameraPosition={cameraPosition}
                    onNavigateToArea={(area) => {
                      if (area.sweep_id) {
                        navigateToSweep(area.sweep_id, { rotation: area.rotation });
                      }
                    }}
                    isSDKConnected={isSDKConnected}
                  />
                </ScrollArea>
              </TabsContent>

              <TabsContent value="nav" className="flex-1 min-h-0 px-4 pb-4">
                <ScrollArea className="h-full">
                  <ViewPositionManager
                    sweeps={sweeps}
                    currentSweep={currentSweep}
                    cameraPosition={cameraPosition}
                    onNavigate={navigateToSweep}
                    onNavigateToLocation={navigateToModelLocation}
                    matterportTourStops={matterportTourStops}
                    matterportLabels={matterportLabels}
                    tags={tags}
                    overlayButtonsEnabled={settings?.overlayButtons?.enabled !== false}
                    getZoom={getZoom}
                  />
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="positions" className="flex-1 min-h-0 px-4 pb-4">
                <ScrollArea className="h-full">
                  <SweepsList
                    sweeps={sweeps}
                    currentSweep={currentSweep}
                    onNavigate={navigateToSweep}
                  />
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}

      {/* Guided Tour Dialog */}
      <GuidedTour
        isOpen={isGuidedTourOpen}
        onClose={() => setIsGuidedTourOpen(false)}
        sweeps={sweeps}
        onNavigate={navigateToSweep}
        currentSweep={currentSweep}
        transitionSpeed={settings?.tour?.speed || 1.5}
      />

      {/* Active Issues Panel (New Tickets System) - Renders with 3D position data */}
      {isAuthenticated && (
        <TicketsPanel
          isOpen={isActiveIssuesPanelOpen}
          onClose={onCloseActiveIssuesPanel}
          currentSweep={currentSweep}
          cameraPosition={cameraPosition}
          onNavigateToTicket={navigateToSweep}
        />
      )}

      {/* Assets Panel (formerly Equipment) - Renders with 3D position data */}
      {isAuthenticated && (
        <EquipmentPanel
          isOpen={isAssetsPanelOpen}
          onClose={onCloseAssetsPanel}
          currentSweep={currentSweep}
          cameraPosition={cameraPosition}
          onNavigateToEquipment={navigateToSweep}
          title="Assets"
        />
      )}

      {/* Buildings Panel (NEW) */}
      {isAuthenticated && (
        <BuildingsPanel
          isOpen={isBuildingsPanelOpen}
          onClose={onCloseBuildingsPanel}
          currentSweep={currentSweep}
          cameraPosition={cameraPosition}
          onNavigateToBuilding={navigateToSweep}
        />
      )}

      {/* Instrumentation Panel (NEW - submenu of Assets) */}
      {isAuthenticated && (
        <InstrumentationPanel
          isOpen={isInstrumentationPanelOpen}
          onClose={onCloseInstrumentationPanel}
          currentSweep={currentSweep}
          cameraPosition={cameraPosition}
          onNavigateToInstrument={navigateToSweep}
        />
      )}

      {/* Compliance Panel (formerly Calibration) - Track equipment calibration schedules */}
      {isAuthenticated && (
        <CompliancePanel
          isOpen={isCompliancePanelOpen}
          onClose={onCloseCompliancePanel}
          currentSweep={currentSweep}
          cameraPosition={cameraPosition}
          onNavigate={navigateToSweep}
        />
      )}

      {/* Reference Panel (formerly Drawings) */}
      <DrawingsPanel
        isOpen={isReferencePanelOpen}
        onClose={onCloseReferencePanel}
        title="Reference"
      />

      {/* Workflows Panel (formerly Procedures) */}
      <ProceduresPanel
        isOpen={isWorkflowsPanelOpen}
        onClose={onCloseWorkflowsPanel}
        title="Workflows"
      />

      {/* Planned Changes Panel (NEW) */}
      <PlannedChangesPanel
        isOpen={isPlannedChangesPanelOpen}
        onClose={onClosePlannedChangesPanel}
        currentSweep={currentSweep}
        cameraPosition={cameraPosition}
        pointerIntersection={pointerIntersection}
        onNavigate={navigateToSweep}
      />

      {/* Risk & Exposure Panel (NEW) */}
      <RiskExposurePanel
        isOpen={isRiskExposurePanelOpen}
        onClose={onCloseRiskExposurePanel}
        currentSweep={currentSweep}
        cameraPosition={cameraPosition}
        onNavigate={navigateToSweep}
      />

      {/* Competency Panel (NEW) */}
      <CompetencyPanel
        isOpen={isCompetencyPanelOpen}
        onClose={onCloseCompetencyPanel}
        currentSweep={currentSweep}
        cameraPosition={cameraPosition}
        onNavigate={navigateToSweep}
      />

      {/* Training Modules Panel - For training industry tours */}
      {isAuthenticated && (
        <TrainingModulesPanel
          isOpen={isTrainingModulesPanelOpen}
          onClose={onCloseTrainingModulesPanel}
          currentTour={currentTour}
          currentSweep={currentSweep}
          cameraPosition={cameraPosition}
          onNavigate={navigateToSweep}
        />
      )}

      {/* Maintenance Procedures Panel - For training industry tours */}
      {isAuthenticated && (
        <MaintenanceProceduresPanel
          isOpen={isMaintenanceProceduresPanelOpen}
          onClose={onCloseMaintenanceProceduresPanel}
          currentTour={currentTour}
          currentSweep={currentSweep}
          cameraPosition={cameraPosition}
          onNavigate={navigateToSweep}
        />
      )}

      {/* Maintenance Dashboard - For training industry tours */}
      {isAuthenticated && (
        <MaintenanceDashboard
          isOpen={isMaintenanceDashboardOpen}
          onClose={onCloseMaintenanceDashboard}
          currentTour={currentTour}
          initialTab={maintenanceDashboardTab}
        />
      )}

      {/* Progress Panel for E-Learning */}
      {isAuthenticated && isTrainingTour && (
        <ProgressPanel
          tourId={currentTour?.id}
          isOpen={isProgressPanelOpen}
          onClose={() => setIsProgressPanelOpen(false)}
          modules={[]}
          quizzes={quizzes}
          onStartQuiz={(quiz) => {
            setActiveQuiz(quiz);
            setIsInteractiveQuizActive(true);
            setIsProgressPanelOpen(false);
          }}
          onGoToModule={(module) => {
            if (module.sweep_id) {
              navigateToSweep(module.sweep_id);
            }
            setIsProgressPanelOpen(false);
          }}
        />
      )}

      {/* Interactive Quiz Overlay - Available for ALL tour types with quizzes */}
      {console.log('Quiz overlay check:', { isAuthenticated, activeInteractiveQuiz: !!activeInteractiveQuiz, isInteractiveQuizActive })}
      {isAuthenticated && (activeInteractiveQuiz || isInteractiveQuizActive) && (
        <InteractiveQuizOverlay
          isOpen={!!activeInteractiveQuiz || isInteractiveQuizActive}
          onClose={() => {
            onCloseInteractiveQuiz?.();
            setIsInteractiveQuizActive(false);
            setActiveQuiz(null);
          }}
          quiz={activeInteractiveQuiz || activeQuiz}
          tourId={currentTour?.id}
          sdkInstance={sdkInstance}
          pointerIntersection={pointerIntersection}
          navigateToSweep={navigateToSweep}
          onQuizComplete={(result) => {
            console.log('Quiz completed:', result);
            onCloseInteractiveQuiz?.();
            setIsInteractiveQuizActive(false);
            setActiveQuiz(null);
          }}
        />
      )}
    </div>
  );
}

export default MatterportViewer;
